import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAppDispatch } from "shared/lib/redux";

import { authStore } from "../../model/auth.store";
import { authService } from "../auth.api";

export const CURRENT_USER_QUERY_KEY = "current_user_query";

type UseCurrentUserQueryReturnType = {
  isAuthResolved: boolean;
};

/**
 * Resolves who is looking at the page and puts it in the store.
 *
 * The widget used to assume everyone was signed in, so guests saw the whole
 * saved-shipments UI and every follow silently failed. A 401 here is the
 * normal answer for a guest, not an error worth retrying.
 */
export const useCurrentUserQuery = (): UseCurrentUserQueryReturnType => {
  const dispatch = useAppDispatch();

  const { data, isError, isSuccess } = useQuery({
    queryKey: [CURRENT_USER_QUERY_KEY],
    queryFn: ({ signal }) => authService.me(signal),
    retry: 0,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!isSuccess && !isError) return;

    dispatch(authStore.actions.setIsAuthenticatedAction(isSuccess && !!data?.data?.id));
  }, [isSuccess, isError, data, dispatch]);

  return {
    // Until this settles the panel must not decide what to show: rendering the
    // guest view first and swapping it makes the page flicker on every load.
    isAuthResolved: isSuccess || isError,
  };
};
